/**
 * sincronizarPush — a árvore de decisão do push deste aparelho (plano "Push: religar sozinho ou avisar", 29/10/2026).
 *
 * A cada abertura do app, volta ao foco, aviso do service worker ou abertura de Preferências, decide UMA entre: nada, confirmar, adotar,
 * renovar, religar ou avisar. As regras valem pela ORDEM (a primeira que casa decide):
 *
 *   0. Entrada: sem suporte → `sem-suporte`; já há uma execução deste `basePath` em voo → a mesma promessa (menos para `mensagem-do-sw`
 *      e `preferencias`, que agendam UMA execução nova logo depois dela); intervalo mínimo, espera depois de falha ou lock de outra
 *      aba → `pulada`.
 *   1. O push do aparelho é do outro app (`podeRenovarOPush`) → `push-em-outro-app`. Sem rede e sem aviso: a permissão é uma só para os
 *      dois apps, e quem fala é o dono.
 *   2. Permissão `default`/`denied`: com a intenção `ligado`, `aviso`; sem ela, `nada-a-fazer`. Sem rede.
 *   3. Permissão `granted` e nenhuma inscrição no navegador: intenção `desligado` → `desligado-de-proposito`; `ligado` → religar;
 *      sem intenção (aparelho de antes deste deploy, Decisão 4) → a religação única, em silêncio.
 *   4. Com inscrição: `POST /verificar` (só leitura). Rede/5xx → `falhou`, e nada mais é tocado.
 *   5. O servidor conhece o endpoint → confirma (confirmação, intenção `ligado`, dono) → `ok`.
 *   6. Não conhece, mas ele o confirmou antes (apagou uma linha que tinha) → renova: `unsubscribe` da velha, `subscribe`, POST.
 *   7. Não conhece e nunca o confirmou (login depois de Sair, aparelho antigo) → adota o endpoint vivo, sem trocar de endpoint.
 *   8. Saída: grava o carimbo (menos em `falhou`, e menos se um "Sair" aconteceu no meio: `aindaVale() === false`), limpa os silêncios em
 *      `ok`/`religado` e avisa a tela.
 *
 * O Desligar nada desfaz, nem na janela residual (Ruling 6): o religar relê a intenção antes do `subscribe` (em
 * `inscreverEsteAparelho`), nenhuma gravação passa por cima de um `desligado` (o `aindaVale` composto abaixo), e quem encontra uma
 * inscrição de pé com a intenção `desligado` — na entrada da regra 4, depois do `verificar` ou depois de um POST — a desfaz no
 * navegador e no servidor (melhor esforço) e devolve `desligado-de-proposito`.
 *
 * Prazos (nenhuma chamada ao navegador prende a aba): `PRAZO_DA_CHAMADA_AO_NAVEGADOR_MS` (10 s) para a leitura da inscrição e o
 * `unsubscribe()` da compensação, e `PRAZO_DA_INSCRICAO_MS` (60 s) para o `inscreverEsteAparelho` inteiro, no religar, na renovação e
 * na religação única. Estourar cai nos `catch` de sempre: `falhou` (no religar, conta para o aviso da 3ª falha seguida), o aviso
 * `precisa-de-clique` na religação única, e o `DELETE` da compensação segue. O axios e o `ready` do SW já têm prazo próprio.
 *
 * Nunca lança: o que for inesperado vira `falhou`. O evento `seweb:push-mudou` só SAI daqui; este módulo não escuta nada, para o evento
 * nunca disparar outra sincronização (Review Focus 4).
 */
import { clienteDoPush } from "@/hooks/clienteDoPush";

import {
  appDoPush,
  lerDonoDoPush,
  marcarDonoDoPush,
  podeRenovarOPush,
  type AppDoPush,
} from "./donoDoPush";
import {
  DesligadoDePropositoError,
  enviarInscricaoAoServidor,
  inscreverEsteAparelho,
} from "./inscreverEsteAparelho";
import {
  FALHAS_ATE_AVISAR,
  avisarQueOPushMudou,
  jaTentouOReligarUnico,
  lerConfirmacaoDoPush,
  lerIntencaoDoPush,
  lerUltimaSincronizacao,
  limparSilencios,
  marcaDoEndpoint,
  QUAIS_AVISOS_DO_PUSH,
  registrarConfirmacaoDoPush,
  registrarFalhaDoReligar,
  registrarIntencaoDoPush,
  registrarReligarUnico,
  registrarSincronizacao,
  zerarFalhasDoReligar,
} from "./memoriaDoPush";

/** Por que a sincronização foi pedida: os mínimos entre execuções dependem disso. */
export type MotivoDaSincronizacao = "abertura" | "foco" | "mensagem-do-sw" | "preferencias";

/** O que o aviso da tela precisa dizer. A lista mora em `memoriaDoPush.ts` (o `limparSilencios` zera o "já mostrado" de cada um). */
export type QualAviso = (typeof QUAIS_AVISOS_DO_PUSH)[number];

export type ResultadoDaSincronizacao =
  | { tipo: "pulada" }
  | { tipo: "sem-suporte" }
  | { tipo: "nada-a-fazer" }
  | { tipo: "ok" }
  | { tipo: "religado"; origem: "automatico" | "renovada" }
  | { tipo: "push-em-outro-app" }
  | { tipo: "desligado-de-proposito" }
  | { tipo: "aviso"; qual: QualAviso }
  | { tipo: "falhou" };

/** Intervalo mínimo desde a última sincronização do app (Decisão 6). SW e Preferências não têm mínimo. */
export const INTERVALO_MINIMO_MS: Record<MotivoDaSincronizacao, number> = {
  abertura: 60_000,
  foco: 1_800_000,
  "mensagem-do-sw": 0,
  preferencias: 0,
};

/** Depois de um `falhou`, abertura e foco esperam isto antes de perguntar de novo (nesta aba). */
export const ESPERA_DEPOIS_DE_FALHA_MS = 120_000;

/**
 * Prazo de cada chamada ao NAVEGADOR que não tem prazo próprio: a leitura da inscrição (`getRegistration` + `getSubscription`, juntas) e
 * o `unsubscribe()` da compensação do Desligar. Estourando, a chamada vira erro e cai nos `catch` de sempre (`falhou`, ou o `DELETE` da
 * compensação segue). Sem prazo, uma chamada que nunca resolve prenderia a promessa em voo da aba (todo gatilho receberia a mesma
 * promessa pendurada) e o lock entre abas (toda outra aba ficaria `pulada`), e a contagem de falhas nunca andaria: o aviso nunca sairia.
 */
export const PRAZO_DA_CHAMADA_AO_NAVEGADOR_MS = 10_000;

/**
 * Prazo do `inscreverEsteAparelho` inteiro no religar, na renovação e na religação única: ele soma service worker pronto (até 10 s),
 * chave VAPID, `unsubscribe()` da velha, `subscribe()` e POST. Estourando, conta como falha do religar (a 3ª seguida vira o aviso); na
 * religação única vira o aviso com o botão. A corrente abandonada pode terminar depois: o que ela grava é verdade (só grava depois de um
 * POST que deu certo, sob o `aindaVale`), e a compensação do Desligar segue valendo.
 */
export const PRAZO_DA_INSCRICAO_MS = 60_000;

export interface OpcoesDaSincronizacao {
  /** Relógio injetável (epoch ms). Padrão: `Date.now`. */
  agora?: () => number;
  /**
   * `false` = a sessão acabou no meio (um "Sair"): nada de dono, intenção ou confirmação é gravado, e a saída não grava o carimbo nem a
   * falha da aba (o login seguinte sincroniza na hora). Padrão: sempre vale.
   */
  aindaVale?: () => boolean;
}

/** Mesmo service worker que o resto do app registra (`inscreverEsteAparelho.ts`, `lib/sw-registration.ts`). */
const SERVICE_WORKER_URL = "/service-worker.js";

const PULADA: ResultadoDaSincronizacao = { tipo: "pulada" };
const FALHOU: ResultadoDaSincronizacao = { tipo: "falhou" };
const DESLIGADO_DE_PROPOSITO: ResultadoDaSincronizacao = { tipo: "desligado-de-proposito" };
const PRECISA_DE_CLIQUE: ResultadoDaSincronizacao = { tipo: "aviso", qual: "precisa-de-clique" };

/** A execução em voo de cada `basePath`: o segundo chamador recebe a MESMA promessa (uma verificação só por vez, por aba). */
const emVoo = new Map<string, Promise<ResultadoDaSincronizacao>>();

/**
 * A execução NOVA que espera a em voo terminar, de cada `basePath` (M3): no máximo uma. Quem chega com intervalo mínimo 0
 * (`mensagem-do-sw`, `preferencias`) enquanto outra roda anuncia estado novo, e o que a execução em voo já leu pode estar velho; as que
 * chegarem depois, antes de ela começar, se juntam nesta.
 */
interface Agendada {
  promessa: Promise<ResultadoDaSincronizacao>;
  /** Começa a execução (quem chama é o fim da que estava em voo) e resolve `promessa` com o resultado dela. */
  iniciar: () => void;
}
const agendadas = new Map<string, Agendada>();

/**
 * Apps que já tentaram a religação única NESTA aba. Vale em OR com a marca do `localStorage` (Ruling 5): com o armazenamento
 * inutilizável (aba privada, bloqueado) a marca não persiste, e sem isto a religação única se repetiria a cada gatilho.
 */
const religouUmaVezNestaAba = new Set<AppDoPush>();

/** Quando (epoch ms) cada app falhou pela última vez NESTA aba. O `falhou` não grava carimbo no aparelho: a espera é só da aba. */
const ultimaFalhaDaAba = new Map<AppDoPush, number>();

/** Só para os testes isolarem os casos: esquece a execução em voo, a religação única da aba e as falhas da aba. */
export function _reiniciarParaTeste(): void {
  emVoo.clear();
  agendadas.clear();
  religouUmaVezNestaAba.clear();
  ultimaFalhaDaAba.clear();
}

/** A chamada ao navegador não respondeu no prazo. O nome próprio deixa o erro reconhecível; ele cai nos `catch` de sempre. */
class PrazoEstouradoError extends Error {
  constructor(oQue: string, ms: number) {
    super(`${oQue} não respondeu em ${ms / 1000} s`);
    this.name = "PrazoEstouradoError";
  }
}

/**
 * Corre a `promessa` contra um prazo de `ms`: estourando, rejeita com `PrazoEstouradoError`. A promessa abandonada não é cancelada (o
 * navegador não deixa) e pode terminar depois, sem efeito aqui (o `race` já a escutou: sua rejeição tardia não vira rejeição solta).
 * O temporizador sempre é desfeito, ganhe quem ganhar.
 */
function comPrazo<T>(promessa: Promise<T>, ms: number, oQue: string): Promise<T> {
  let temporizador: ReturnType<typeof setTimeout> | undefined;
  const estouro = new Promise<never>((_, rejeitar) => {
    temporizador = setTimeout(() => rejeitar(new PrazoEstouradoError(oQue, ms)), ms);
  });
  return Promise.race([promessa, estouro]).finally(() => clearTimeout(temporizador));
}

/**
 * Sincroniza o push deste aparelho com o navegador e o servidor (ver o topo do arquivo). Nunca rejeita.
 * Não é `async` de propósito: a mesma execução em voo devolve a MESMA promessa.
 *
 * Com uma execução deste `basePath` em voo:
 *  - motivo de intervalo mínimo > 0 (`abertura`, `foco`): recebe a promessa dela, como sempre;
 *  - motivo de intervalo 0 (`mensagem-do-sw`, `preferencias`): é estado novo, e a em voo pode ter lido o navegador de antes (e o carimbo
 *    dela seguraria o foco seguinte por 30 min). Agenda UMA execução nova para logo depois da em voo; as que chegarem no meio se juntam
 *    nela (no máximo uma pendente por `basePath`, então não há laço) e todas recebem a promessa dessa execução.
 */
export function sincronizarPush(
  basePath: string,
  motivo: MotivoDaSincronizacao,
  opcoes: OpcoesDaSincronizacao = {}
): Promise<ResultadoDaSincronizacao> {
  const emAndamento = emVoo.get(basePath);
  if (!emAndamento) return iniciar(basePath, motivo, opcoes);
  if (INTERVALO_MINIMO_MS[motivo] > 0) return emAndamento;
  const jaAgendada = agendadas.get(basePath);
  if (jaAgendada) return jaAgendada.promessa;
  return agendar(basePath, motivo, opcoes);
}

/** Começa uma execução e a registra como a em voo do `basePath`; no fim, tira o registro e dispara a agendada, se houver (M3). */
function iniciar(
  basePath: string,
  motivo: MotivoDaSincronizacao,
  opcoes: OpcoesDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  const promessa: Promise<ResultadoDaSincronizacao> = executar(basePath, motivo, opcoes).finally(
    () => {
      if (emVoo.get(basePath) === promessa) emVoo.delete(basePath);
      // No mesmo passo síncrono em que o registro sai: nenhum chamador enxerga um intervalo sem execução e sem agendada.
      const proxima = agendadas.get(basePath);
      if (proxima) {
        agendadas.delete(basePath);
        proxima.iniciar();
      }
    }
  );
  emVoo.set(basePath, promessa);
  return promessa;
}

function agendar(
  basePath: string,
  motivo: MotivoDaSincronizacao,
  opcoes: OpcoesDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  let iniciarAgendada!: () => void;
  const promessa = new Promise<ResultadoDaSincronizacao>((resolver) => {
    iniciarAgendada = () => resolver(iniciar(basePath, motivo, opcoes));
  });
  agendadas.set(basePath, { promessa, iniciar: iniciarAgendada });
  return promessa;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Regra 0: a entrada
// ---------------------------------------------------------------------------------------------------------------------------------

function temSuporte(): boolean {
  return (
    typeof Notification !== "undefined" &&
    typeof PushManager !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator
  );
}

/** Passou MENOS de `limite` desde `desde`? Um `desde` no futuro (relógio do aparelho que voltou) não segura nada. */
function passouMenosQue(agora: number, desde: number, limite: number): boolean {
  const decorrido = agora - desde;
  return decorrido >= 0 && decorrido < limite;
}

/**
 * Abertura e foco não repetem à toa: nem dentro do intervalo do carimbo — que SÓ vale se a permissão de agora for a mesma do carimbo
 * (quem corrigiu a permissão e recarregou sincroniza na hora; sem carimbo nunca pula) —, nem dentro da espera depois de uma falha.
 */
function deveSerPulada(
  app: AppDoPush,
  motivo: MotivoDaSincronizacao,
  agora: () => number
): boolean {
  if (motivo !== "abertura" && motivo !== "foco") return false;
  const carimbo = lerUltimaSincronizacao(app);
  if (
    carimbo !== null &&
    carimbo.permissao === Notification.permission &&
    passouMenosQue(agora(), carimbo.em, INTERVALO_MINIMO_MS[motivo])
  ) {
    return true;
  }
  const falhouEm = ultimaFalhaDaAba.get(app);
  return falhouEm !== undefined && passouMenosQue(agora(), falhouEm, ESPERA_DEPOIS_DE_FALHA_MS);
}

async function executar(
  basePath: string,
  motivo: MotivoDaSincronizacao,
  opcoes: OpcoesDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  // Sem suporte não há permissão para carimbar; a tela é avisada do mesmo jeito (regra 8: toda execução que não foi pulada).
  if (!temSuporte()) {
    avisarQueOPushMudou();
    return { tipo: "sem-suporte" };
  }
  const app = appDoPush(basePath);
  const agora = opcoes.agora ?? Date.now;
  if (deveSerPulada(app, motivo, agora)) return PULADA;

  try {
    // Outra aba sincronizando este app: `pulada` (ela grava o carimbo, e a próxima abertura já o enxerga). O lock fica preso até o fim
    // da execução, carimbo e evento incluídos. Sem `navigator.locks`, vale só a promessa compartilhada da aba.
    const locks = navigator.locks as LockManager | undefined;
    if (!locks) return await sincronizar(basePath, app, agora, opcoes);
    return await locks.request(`seweb-push-sync:${app}`, { ifAvailable: true }, (lock) =>
      lock ? sincronizar(basePath, app, agora, opcoes) : PULADA
    );
  } catch {
    // O pedido do lock em si falhou (contexto sem permissão para ele): conta como falha desta aba, nada mais.
    return concluir(app, FALHOU, Notification.permission, agora, opcoes.aindaVale);
  }
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Regra 8: a saída
// ---------------------------------------------------------------------------------------------------------------------------------

/**
 * `permissao` é a que a decisão VIU (lida na entrada), não a de agora: se ela mudou no meio, o carimbo guarda a antiga e a próxima
 * execução, vendo outra, ignora o intervalo.
 *
 * `aindaVale() === false` (um "Sair" no meio): nem o carimbo nem a falha da aba são gravados. O Sair já apagou o carimbo (e o
 * `soltarDonoDoPush` é o que garante que o login seguinte sincronize na hora); regravá-lo aqui, ou deixar a falha da aba, faria a
 * abertura de quem entra de novo ser `pulada` (60 s; 2 min depois de falha) e o foco por 30 min, com a linha do servidor já apagada.
 * O evento continua saindo: a tela relê o estado do mesmo jeito.
 */
function concluir(
  app: AppDoPush,
  resultado: ResultadoDaSincronizacao,
  permissao: string,
  agora: () => number,
  aindaVale?: () => boolean
): ResultadoDaSincronizacao {
  if (aindaVale?.() !== false) {
    if (resultado.tipo === "falhou") ultimaFalhaDaAba.set(app, agora());
    else registrarSincronizacao(app, agora(), permissao);
  }
  if (resultado.tipo === "ok" || resultado.tipo === "religado") limparSilencios();
  avisarQueOPushMudou();
  return resultado;
}

async function sincronizar(
  basePath: string,
  app: AppDoPush,
  agora: () => number,
  opcoes: OpcoesDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  const permissao = Notification.permission;
  let resultado: ResultadoDaSincronizacao;
  try {
    resultado = await decidir(basePath, app, permissao, opcoes);
  } catch {
    // Fonte externa nunca quebra o código: o navegador ou a rede fizeram algo que a árvore não previu.
    resultado = FALHOU;
  }
  return concluir(app, resultado, permissao, agora, opcoes.aindaVale);
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Regras 1 a 7: a decisão
// ---------------------------------------------------------------------------------------------------------------------------------

async function decidir(
  basePath: string,
  app: AppDoPush,
  permissao: NotificationPermission,
  opcoes: OpcoesDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  // Regra 1.
  if (!podeRenovarOPush(app)) return { tipo: "push-em-outro-app" };

  // Regra 2.
  if (permissao !== "granted") {
    if (lerIntencaoDoPush() !== "ligado") return { tipo: "nada-a-fazer" };
    return {
      tipo: "aviso",
      qual: permissao === "denied" ? "bloqueado" : "permissao-voltou-a-perguntar",
    };
  }

  // Toda gravação dos caminhos que inscrevem passa por este `aindaVale`: a sessão ainda vale E o Desligar não foi gravado no meio.
  const aindaVale = () => (opcoes.aindaVale?.() ?? true) && lerIntencaoDoPush() !== "desligado";

  const sub = await lerInscricaoDoNavegador();
  return sub === null
    ? decidirSemInscricao(basePath, app, aindaVale)
    : decidirComInscricao(basePath, app, sub, aindaVale, opcoes);
}

/**
 * A inscrição do navegador para este service worker; sem registro de SW também é "sem inscrição". A leitura inteira tem prazo
 * (`PRAZO_DA_CHAMADA_AO_NAVEGADOR_MS`): estourando, lança e a execução vira `falhou`.
 */
function lerInscricaoDoNavegador(): Promise<PushSubscription | null> {
  return comPrazo(
    (async () => {
      const registro = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
      return (await registro?.pushManager.getSubscription()) ?? null;
    })(),
    PRAZO_DA_CHAMADA_AO_NAVEGADOR_MS,
    "A leitura da inscrição do navegador"
  );
}

/** Regra 3: permissão dada e nenhuma inscrição no navegador. */
async function decidirSemInscricao(
  basePath: string,
  app: AppDoPush,
  aindaVale: () => boolean
): Promise<ResultadoDaSincronizacao> {
  const intencao = lerIntencaoDoPush();
  if (intencao === "desligado") return DESLIGADO_DE_PROPOSITO;
  if (intencao === "ligado") return religar(basePath, app, "automatico", aindaVale);
  return religarUmaVez(basePath, app, aindaVale);
}

/**
 * Aparelho de antes deste deploy (permissão dada, sem inscrição, sem intenção — Decisão 4): uma tentativa, sozinha e em silêncio. A marca
 * é gravada ANTES de tentar, e qualquer falha vira o aviso com o botão, na hora, sem contar falhas e sem segunda tentativa.
 */
async function religarUmaVez(
  basePath: string,
  app: AppDoPush,
  aindaVale: () => boolean
): Promise<ResultadoDaSincronizacao> {
  if (religouUmaVezNestaAba.has(app) || jaTentouOReligarUnico(app)) return PRECISA_DE_CLIQUE;
  registrarReligarUnico(app);
  religouUmaVezNestaAba.add(app);

  let sub: PushSubscription;
  try {
    sub = await comPrazo(
      inscreverEsteAparelho({ basePath, origem: "automatico", aindaVale }),
      PRAZO_DA_INSCRICAO_MS,
      "A inscrição do aparelho"
    );
  } catch (erro) {
    return erro instanceof DesligadoDePropositoError ? DESLIGADO_DE_PROPOSITO : PRECISA_DE_CLIQUE;
  }
  zerarFalhasDoReligar(app);
  return fecharInscricao(basePath, sub, { tipo: "religado", origem: "automatico" });
}

/**
 * Religar (intenção `ligado`) e renovar (regra 6): `inscreverEsteAparelho` e o tratamento de falhas. `DesligadoDePropositoError` vale
 * `desligado-de-proposito`; `NotAllowedError` pede um gesto (repetir não adianta) e vira o aviso; qualquer outro erro conta, e na
 * `FALHAS_ATE_AVISAR`-ésima seguida vira o aviso — sem isso um serviço de push inalcançável, uma chave VAPID trocada ou um
 * `InvalidStateError` deixariam a pessoa sem push e sem uma palavra na tela. O estouro do `PRAZO_DA_INSCRICAO_MS` (uma chamada do
 * navegador que nunca resolve) e o `NaoDesinscreveuError` (o `unsubscribe()` da velha devolveu `false`) contam como qualquer outra falha.
 */
async function religar(
  basePath: string,
  app: AppDoPush,
  origem: "automatico" | "renovada",
  aindaVale: () => boolean,
  substituir?: PushSubscription
): Promise<ResultadoDaSincronizacao> {
  let sub: PushSubscription;
  try {
    sub = await comPrazo(
      inscreverEsteAparelho({ basePath, origem, substituir, aindaVale }),
      PRAZO_DA_INSCRICAO_MS,
      "A inscrição do aparelho"
    );
  } catch (erro) {
    if (erro instanceof DesligadoDePropositoError) return DESLIGADO_DE_PROPOSITO;
    if (nomeDoErro(erro) === "NotAllowedError") return PRECISA_DE_CLIQUE;
    return registrarFalhaDoReligar(app) >= FALHAS_ATE_AVISAR ? PRECISA_DE_CLIQUE : FALHOU;
  }
  zerarFalhasDoReligar(app);
  return fecharInscricao(basePath, sub, { tipo: "religado", origem });
}

function nomeDoErro(erro: unknown): unknown {
  return typeof erro === "object" && erro !== null ? (erro as { name?: unknown }).name : undefined;
}

/** Regras 4 a 7: há inscrição no navegador. */
async function decidirComInscricao(
  basePath: string,
  app: AppDoPush,
  sub: PushSubscription,
  aindaVale: () => boolean,
  opcoes: OpcoesDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  // Ruling 6 (b): inscrição de pé com o Desligar gravado (uma sincronização que passou da checagem, ou outra aba) → desfaz, sem perguntar.
  // O clique em Ligar não cai aqui: ele apaga o `desligado` antes de inscrever (`usePushSubscription.subscribe`, Ruling 7).
  if (lerIntencaoDoPush() === "desligado") return desfazerPorqueDesligou(basePath, sub);

  // Regra 4: só leitura. Erro de rede/5xx, ou resposta sem `conhecida` booleano, não toca em mais nada.
  let conhecida: boolean;
  try {
    const { data } = await clienteDoPush(basePath).post<{ conhecida?: unknown }>(
      `${basePath}/verificar`,
      { endpoint: sub.endpoint }
    );
    if (typeof data?.conhecida !== "boolean") return FALHOU;
    conhecida = data.conhecida;
  } catch {
    return FALHOU;
  }

  // Regra 5: o servidor conhece o endpoint.
  if (conhecida) {
    // Um Desligar gravado durante o `verificar` não pode ser coberto por um `ligado` (mesma janela do Ruling 6).
    if (lerIntencaoDoPush() === "desligado") return desfazerPorqueDesligou(basePath, sub);
    // Um "Sair" durante o `verificar` não deixa marca órfã (dono sem sessão por trás).
    if (opcoes.aindaVale?.() !== false) {
      registrarConfirmacaoDoPush(app, marcaDoEndpoint(sub.endpoint));
      registrarIntencaoDoPush("ligado");
      if (lerDonoDoPush() === null) marcarDonoDoPush(app);
    }
    return { tipo: "ok" };
  }

  // Regra 6: o servidor não conhece um endpoint que ele mesmo confirmou → apagou uma linha que tinha → renova.
  if (lerConfirmacaoDoPush(app) === marcaDoEndpoint(sub.endpoint)) {
    return religar(basePath, app, "renovada", aindaVale, sub);
  }

  // Regra 7: nunca o confirmou (login depois de Sair, aparelho antigo) → adota o endpoint vivo, sem trocar.
  try {
    await enviarInscricaoAoServidor(basePath, sub, "automatico", { aindaVale });
  } catch {
    return FALHOU;
  }
  return fecharInscricao(basePath, sub, { tipo: "religado", origem: "automatico" });
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Ruling 6: o Desligar que chegou no meio
// ---------------------------------------------------------------------------------------------------------------------------------

/**
 * Depois de todo religar, renovar ou adotar que postou: relê a intenção. `desligado` (a pessoa apertou Desligar enquanto o
 * `subscribe` ou o POST voavam; o `aindaVale` composto já impediu a gravação de `ligado`) → desfaz a inscrição que ficou de pé.
 */
async function fecharInscricao(
  basePath: string,
  sub: PushSubscription,
  resultado: ResultadoDaSincronizacao
): Promise<ResultadoDaSincronizacao> {
  if (lerIntencaoDoPush() === "desligado") return desfazerPorqueDesligou(basePath, sub);
  return resultado;
}

/**
 * `unsubscribe()` da inscrição que ficou de pé e `DELETE` da linha, ambos em melhor esforço (um erro não lança nem impede o outro).
 * Nada aqui grava `ligado`. Devolve `desligado-de-proposito`.
 */
async function desfazerPorqueDesligou(
  basePath: string,
  sub: PushSubscription
): Promise<ResultadoDaSincronizacao> {
  const endpoint = sub.endpoint;
  try {
    await comPrazo(
      sub.unsubscribe(),
      PRAZO_DA_CHAMADA_AO_NAVEGADOR_MS,
      "O unsubscribe do navegador"
    );
  } catch {
    // o navegador recusou (ou não respondeu no prazo): o DELETE abaixo ainda tira a linha do servidor, e a próxima sincronização repete
    // a compensação
  }
  try {
    await clienteDoPush(basePath).delete(`${basePath}/subscribe`, { data: { endpoint } });
  } catch {
    // 404 (a linha já não existe) ou rede: sem linha, o próximo envio dá 410 e o worker a apaga
  }
  return DESLIGADO_DE_PROPOSITO;
}
